import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { catchError, Observable, of } from 'rxjs';
import { Menu } from '../models/menu';
import { ServiceConstant } from '../utils/service-constant.util';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class MenuService {
  httpClient = inject(HttpClient);

  token = localStorage.getItem('userToken') || '';

  headers = new HttpHeaders({
    Authorization: `Bearer ${this.token}`,
    x_api_key: environment.apiKey,
    x_client_type: 'web',
  });

  constructor() {}

  getMenusByStatus(status?: any): Observable<any> {
    return this.httpClient
      .get<Menu[]>(ServiceConstant.apiUrl + '/restaurants/menus/by-status?status=' + status)
      .pipe(
        catchError((error) => {
          console.error('Error fetching menus by status:', error);
          return of(error);
        }),
      );
  }

  getMenus(): Observable<any> {
    return this.httpClient.get<Menu[]>(ServiceConstant.apiUrl + '/restaurants/menus').pipe(
      catchError((error) => {
        console.error('Error fetching all menus:', error);
        return of(error);
      }),
    );
  }

  getServingTime(): Observable<any> {
    return this.httpClient.get<Menu[]>(ServiceConstant.apiUrl + '/restaurants/serving_times').pipe(
      catchError((error) => {
        console.error('Error fetching all serving times:', error);
        return of(error);
      }),
    );
  }

  addServingTime(payload: any): Observable<any> {
    return this.httpClient
      .post<any>(ServiceConstant.apiUrl + '/restaurants/serving_times', payload)
      .pipe(
        catchError((error) => {
          console.error('Error fetching new serving time:', error);
          return of(error);
        }),
      );
  }

  getMenuById(menuId: any): Observable<any> {
    return this.httpClient
      .get<Menu[]>(ServiceConstant.apiUrl + '/restaurants/menus/' + menuId)
      .pipe(
        catchError((error) => {
          console.error('Error fetching menus:', error);
          return of(error);
        }),
      );
  }

  getDescription(menu: string): Observable<any> {
    return this.httpClient
      .get<Menu[]>(
        ServiceConstant.apiUrl +
          '/generative/dish/description?num_of_characters=120&dish_name=' +
          menu,
      )
      .pipe(
        catchError((error) => {
          console.error('Error fetching menus:', error);
          return of(error);
        }),
      );
  }

  getIngredients(menu: string): Observable<any> {
    return this.httpClient
      .get<Menu[]>(ServiceConstant.apiUrl + '/generative/dish/ingredients?dish_description=' + menu)
      .pipe(
        catchError((error) => {
          console.error('Error fetching menu ingredients:', error);
          return of(error);
        }),
      );
  }

  getTags(menu: string): Observable<any> {
    return this.httpClient
      .get<Menu[]>(ServiceConstant.apiUrl + '/generative/dish/tags?dish_name=' + menu)
      .pipe(
        catchError((error) => {
          console.error('Error fetching menu tag:', error);
          return of(error);
        }),
      );
  }

  getPreparationTime(menu: string, description: string): Observable<any> {
    // HttpParams encodes the values: item names and descriptions can hold & or #.
    const params = new HttpParams().set('dish_name', menu).set('dish_description', description);
    return this.httpClient
      .get<any>(ServiceConstant.apiUrl + '/generative/dish/preparation-time', { params })
      .pipe(
        catchError((error) => {
          console.error('Error fetching menu preparation time:', error);
          return of(error);
        }),
      );
  }

  addMenuImage(formData: FormData): Observable<any> {
    return this.httpClient
      .post<any>(ServiceConstant.apiUrl + '/commons/file/upload', formData)
      .pipe(
        catchError((error) => {
          console.error('Error fetching menu tag:', error);
          return of(error);
        }),
      );
  }

  addMenu(menu: any): Observable<any> {
    return this.httpClient.post<any>(ServiceConstant.apiUrl + '/restaurants/menus', menu).pipe(
      catchError((error) => {
        console.error('Error fetching menu tag:', error);
        return of(error);
      }),
    );
  }

  editMenu(menu: any): Observable<any> {
    return this.httpClient
      .put<any>(ServiceConstant.apiUrl + '/restaurants/menus/' + menu.menu_id, menu)
      .pipe(
        catchError((error) => {
          console.error('Error editing menu:', error);
          return of(error);
        }),
      );
  }

  patchMenu(menu: any): Observable<any> {
    return this.httpClient
      .patch<any>(ServiceConstant.apiUrl + '/restaurants/menus/' + menu.menu_id, menu)
      .pipe(
        catchError((error) => {
          console.error('Error patching menu:', error);
          return of(error);
        }),
      );
  }

  menuStockChanges(menus: any): Observable<any> {
    return this.httpClient
      .patch<any>(ServiceConstant.apiUrl + '/restaurants/menus/availability', menus)
      .pipe(
        catchError((error) => {
          console.error('Error editing menu:', error);
          return of(error);
        }),
      );
  }

  deleteMenu(menuId: any): Observable<any> {
    return this.httpClient
      .delete<any>(ServiceConstant.apiUrl + '/restaurants/menus/' + menuId)
      .pipe(
        catchError((error) => {
          console.error('Error fetching menu tag:', error);
          return of(error);
        }),
      );
  }

  getTypesByRestaurant(): Observable<any> {
    return this.httpClient.get<Menu[]>(ServiceConstant.apiUrl + '/restaurants').pipe(
      catchError((error) => {
        console.error('Error fetching menus:', error);
        return of(error);
      }),
    );
  }

  getExitingSKU(): Observable<any> {
    return this.httpClient.get<Menu[]>(ServiceConstant.apiUrl + '/restaurants/menus/get-skus').pipe(
      catchError((error) => {
        console.error('Error fetching menus:', error);
        return of(error);
      }),
    );
  }

  generateMenuCoverImage(menuId: string): Observable<any> {
    return this.httpClient
      .post<any>(ServiceConstant.apiUrl + '/generative/menus/' + menuId + '/image', {})
      .pipe(
        catchError((error) => {
          console.error('Error generating menu cover image:', error);
          return of(error);
        }),
      );
  }
}
